import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Grid,
  Select,
  TextAreaField,
  TextField,
} from '@backstage/ui';
import { ServiceBusSubscription, ServiceBusTopic } from '../types';
import { FieldHint } from '../../../components/FieldHint';
import styles from '../ServiceBusTopicManager.module.css';
import '../../../select-overlay.css';

interface SubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (subscription: ServiceBusSubscription) => void;
  initialData?: ServiceBusSubscription;
  topics: ServiceBusTopic[];
  existingSubscriptions: Array<{ name: string; topic: string; id?: string }>;
  editingId?: string;
  mode: 'create' | 'edit';
  preselectedTopic?: string;
}

export const SubscriptionDialog = ({
  open,
  onClose,
  onSave,
  initialData,
  topics,
  existingSubscriptions,
  editingId,
  mode,
  preselectedTopic,
}: SubscriptionDialogProps) => {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [sqlFilter, setSqlFilter] = useState('');
  const [lockDuration, setLockDuration] = useState('');
  const [maxDeliveryCount, setMaxDeliveryCount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setTopic(initialData.topic);
      setSqlFilter(initialData.sql_filter);
      setLockDuration(initialData.lock_duration || '');
      setMaxDeliveryCount(
        initialData.max_delivery_count !== undefined
          ? String(initialData.max_delivery_count)
          : '',
      );
    } else {
      setName('');
      setTopic(preselectedTopic || '');
      setSqlFilter('');
      setLockDuration('');
      setMaxDeliveryCount('');
    }
    setErrors({});
  }, [initialData, open, preselectedTopic]);

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};

    if (!name.trim()) {
      errs.name = 'Nome é obrigatório';
    } else if (!/^[a-z0-9-_]+$/.test(name)) {
      errs.name =
        'Deve conter apenas letras minúsculas, números, hífens e underscores';
    } else {
      const duplicate = existingSubscriptions.some(
        s => s.topic === topic && s.name === name && s.id !== editingId,
      );
      if (duplicate) {
        errs.name = 'Já existe uma subscription com esse nome neste tópico';
      }
    }

    if (!topic) {
      errs.topic = 'Tópico é obrigatório';
    }

    if (!sqlFilter.trim()) {
      errs.sql_filter = 'Filtro SQL é obrigatório';
    }

    if (lockDuration.trim() && !/^PT\d+[HMS]$/i.test(lockDuration.trim())) {
      errs.lock_duration =
        'Formato inválido. Use o formato ISO 8601 (ex: PT30S, PT5M)';
    }

    if (maxDeliveryCount.trim()) {
      const parsed = parseInt(maxDeliveryCount, 10);
      if (isNaN(parsed) || parsed < 1) {
        errs.max_delivery_count = 'Deve ser um número inteiro positivo';
      }
    }

    return errs;
  };

  const handleSave = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const sub: ServiceBusSubscription = {
      name: name.trim(),
      topic,
      sql_filter: sqlFilter.trim(),
    };

    if (lockDuration.trim()) {
      sub.lock_duration = lockDuration.trim();
    }

    if (maxDeliveryCount.trim()) {
      sub.max_delivery_count = parseInt(maxDeliveryCount, 10);
    }

    onSave(sub);
    onClose();
  };

  return (
    <Dialog
      isOpen={open}
      isDismissable
      className={styles.subscriptionDialog}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogHeader>
        {mode === 'create' ? 'Nova Subscription' : 'Editar Subscription'}
      </DialogHeader>
      <DialogBody>
        <Grid.Root columns={{ sm: '12' }} gap="4">
          <Grid.Item colSpan={{ sm: '12', md: '6' }}>
            <Flex direction="column">
              <TextField
                label="Nome da Subscription"
                size="medium"
                value={name}
                onChange={value => {
                  setName(value);
                  setErrors(prev => ({ ...prev, name: '' }));
                }}
              />
              <FieldHint error={!!errors.name}>
                {errors.name || 'Letras minúsculas, números e hífens'}
              </FieldHint>
            </Flex>
          </Grid.Item>
          <Grid.Item colSpan={{ sm: '12', md: '6' }}>
            <Flex direction="column">
              <Select
                label="Tópico"
                name="topic"
                size="medium"
                value={topic || null}
                onChange={key => {
                  const next = String(key ?? '');
                  const valid = topics.some(item => item.name === next);
                  if (!valid && next) {
                    return;
                  }
                  setTopic(next);
                  setErrors(prev => ({ ...prev, topic: '' }));
                }}
                search={{ placeholder: 'Digite para filtrar...' }}
                options={topics.map(item => ({
                  id: item.name,
                  label: item.name,
                }))}
              />
              <FieldHint error={!!errors.topic}>{errors.topic}</FieldHint>
            </Flex>
          </Grid.Item>
          <Grid.Item colSpan={{ sm: '12' }}>
            <Flex direction="column">
              <TextAreaField
                label="Filtro SQL"
                size="medium"
                value={sqlFilter}
                onChange={value => {
                  setSqlFilter(value);
                  setErrors(prev => ({ ...prev, sql_filter: '' }));
                }}
              />
              <FieldHint error={!!errors.sql_filter}>
                {errors.sql_filter ||
                  "Ex: event in ('reanalyze', 'process') OR command in ('report')"}
              </FieldHint>
            </Flex>
          </Grid.Item>
          <Grid.Item colSpan={{ sm: '12', md: '6' }}>
            <Flex direction="column">
              <TextField
                label="Lock Duration (opcional)"
                size="medium"
                value={lockDuration}
                onChange={value => {
                  setLockDuration(value);
                  setErrors(prev => ({ ...prev, lock_duration: '' }));
                }}
              />
              <FieldHint error={!!errors.lock_duration}>
                {errors.lock_duration || 'Ex: PT30S, PT5M'}
              </FieldHint>
            </Flex>
          </Grid.Item>
          <Grid.Item colSpan={{ sm: '12', md: '6' }}>
            <Flex direction="column">
              <TextField
                label="Max Delivery Count (opcional)"
                size="medium"
                value={maxDeliveryCount}
                onChange={value => {
                  setMaxDeliveryCount(value);
                  setErrors(prev => ({ ...prev, max_delivery_count: '' }));
                }}
              />
              <FieldHint error={!!errors.max_delivery_count}>
                {errors.max_delivery_count || 'Número inteiro positivo'}
              </FieldHint>
            </Flex>
          </Grid.Item>
        </Grid.Root>
      </DialogBody>
      <DialogFooter>
        <Button variant="primary" onPress={handleSave}>
          {mode === 'create' ? 'Criar' : 'Salvar'}
        </Button>
        <Button variant="secondary" slot="close" onPress={onClose}>
          Cancelar
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
